import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import useSnackbar from '../../Utils/useSnackbar';
import { tokens, withAlpha } from '../../Common/designTokens';

const ConfirmCusipDeleteDialog = ({ open, onClose, onConfirm, cusip, isDeleting }) => {
  const { showSuccess } = useSnackbar();
  const [errorMessage, setErrorMessage] = useState('');
  
  // Clear error when dialog closes
  useEffect(() => {
    if (!open) {
      setErrorMessage('');
    }
  }, [open]);
  
  const handleConfirmDelete = () => {
    if (!isDeleting) {
      setErrorMessage(''); // Clear any existing error
      onConfirm();
    }
  };
  
  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };
  
  if (!cusip) return null;

  // Common modal style matching OrderConfirmationModal
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: tokens.colors.bodyBackgroundColor,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };
  
  return (
    <Modal
      open={open}
      onClose={!isDeleting ? handleClose : undefined}
      aria-labelledby="confirm-cusip-delete-modal"
    >
      <Box sx={modalStyle}>
        <Typography id="confirm-cusip-delete-modal-title" variant="h6" component="h2" sx={{ color: tokens.colors.fontColour, mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
          Confirm Delete Position
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: tokens.palette.danger }} />
        </Box>
        
        {/* Display persistent error message if present */}
        {errorMessage && (
          <Box sx={{
            width: '100%',
            mb: 2,
            p: 1.5,
            backgroundColor: withAlpha(tokens.palette.danger, 0.08),
            border: `1px solid ${withAlpha(tokens.palette.danger, 0.3)}`,
            borderRadius: '8px'
          }}>
            <Typography sx={{ color: tokens.palette.danger, textAlign: 'center', fontWeight: 600 }}>
              {errorMessage}
            </Typography>
          </Box>
        )}
        
        <Typography variant="subtitle1" sx={{ color: tokens.colors.fontColour85, textAlign: 'center', mb: 2 }}>
          Are you sure you want to delete this existing position?
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 1, color: tokens.colors.fontColour, textAlign: 'center' }}>
          CUSIP: {cusip}
        </Typography>
        
        <Typography variant="body2" sx={{ color: tokens.palette.danger, mb: 3, textAlign: 'center', fontWeight: 600 }}>
          This action cannot be undone.
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isDeleting}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: tokens.colors.fontColour,
              borderColor: tokens.colors.bodyBorderColor50,
              width: 120,
              borderRadius: '8px',
              '&:hover': {
                borderColor: tokens.colors.bodyBorderColor50,
                backgroundColor: tokens.colors.defaultGridHoverColor,
              },
              '&.Mui-disabled': {
                color: tokens.colors.disabledFontColour,
                borderColor: tokens.colors.bodyBorderColor50,
              }
            }}>
             Cancel
           </Button>
          <Button
            variant="outlined"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              width: 140,
              color: tokens.palette.danger,
              borderColor: tokens.palette.danger,
              borderRadius: '8px',
              '&:hover': { backgroundColor: withAlpha(tokens.palette.danger, 0.08) },
              '&.Mui-disabled': {
                color: withAlpha(tokens.palette.danger, 0.4),
                borderColor: withAlpha(tokens.palette.danger, 0.4),
              }
            }}>
             {isDeleting ? 'Deleting...' : 'Delete'}
           </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmCusipDeleteDialog;
